import { authenticatedFetch } from './client';
import type { ScreenshotMetadata } from '@/types/report';
import type { PackageSummaryResponse } from '@/types/packageSummary';

// Backend RuleResult structure
interface RuleResult {
  passed: boolean;
  message: string;
  details?: string;
  severity: 'red' | 'orange';
  status: 'pending' | 'accepted' | 'approved' | 'dismissed';
  relevant_files?: string[] | null;
  relevant_screenshots?: (string | ScreenshotMetadata)[] | null;
}

// Backend report response
interface BackendReport {
  id: string;
  campaign_package_name: string;
  created_at: string;
  updated_at: string;
  issues: RuleResult[];
  notes?: string | null;
}

// Frontend issue structure
export interface FrontendIssue {
  id: string;
  fileNames: string[];
  category: string;
  flag: 'red' | 'orange';
  timestamps: Array<{ start: string; end: string }>;
  description: string;
  details?: string;
  status: 'pending' | 'approved' | 'dismissed';
  screenshots?: Array<{
    id: string;
    url: string;
    timestamp?: string;
    timestampSeconds?: number;
    frameNumber?: number;
    videoFile?: string;
  }>;
}

// Issue update payload
export interface IssueUpdate {
  passed: boolean;
  message: string;
  details: string;
  severity: 'red' | 'orange';
  relevant_files: string[] | null;
  relevant_screenshots: (string | ScreenshotMetadata)[] | null;
  status: 'pending' | 'approved' | 'dismissed';
}

/**
 * Fetch a report by report ID
 */
export const fetchReportById = async (reportId: string): Promise<{
  reportId: string;
  packageName: string;
  gcsPath: string;
  issues: FrontendIssue[];
  notes: string | null;
}> => {
  console.log('📥 Fetching report by ID:', reportId);

  const response = await authenticatedFetch(
    `/api/reports/${encodeURIComponent(reportId)}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch report: ${response.statusText}`);
  }

  const data: BackendReport = await response.json();
  console.log('📦 Report data:', data);

  // Transform backend issues to frontend format
  const transformedIssues: FrontendIssue[] = data.issues
    .filter((issue) => !issue.passed && issue.status !== 'dismissed')
    .map((issue, index) => {
      // Transform screenshots
      const screenshots = issue.relevant_screenshots?.map((screenshot, screenshotIndex) => {
        if (typeof screenshot === 'object' && 'image_data' in screenshot) {
          return {
            id: `screenshot-${data.id}-${index}-${screenshotIndex}`,
            url: screenshot.image_data,
            timestamp: screenshot.timecode,
            timestampSeconds: screenshot.timestamp_seconds,
            frameNumber: screenshot.frame_number,
            videoFile: screenshot.video_file,
          };
        }
        return {
          id: `screenshot-${data.id}-${index}-${screenshotIndex}`,
          url: screenshot,
        };
      });

      return {
        id: `${data.id}-${index}`,
        fileNames: issue.relevant_files || [],
        category: 'Validation Issue',
        flag: issue.severity,
        timestamps: [],
        description: issue.message,
        details: issue.details,
        status: issue.status === 'accepted' ? 'approved' : issue.status,
        screenshots,
      };
    });

  console.log('✅ Transformed issues:', transformedIssues);

  // Construct GCS path
  const bucketName = 'bkt-vmlmap-agentic-dev-content-agents';
  const gcsPath = `${bucketName}/campaign_packages/${data.campaign_package_name}`;

  return {
    reportId: data.id,
    packageName: data.campaign_package_name,
    gcsPath,
    issues: transformedIssues,
    notes: data.notes ?? null,
  };
};

/**
 * Fetch a report by campaign name (legacy - fetches latest report for campaign)
 */
export const fetchReport = async (campaignName: string): Promise<{
  reportId: string;
  packageName: string;
  gcsPath: string;
  issues: FrontendIssue[];
  notes: string | null;
}> => {
  console.log('📥 Fetching latest report for campaign:', campaignName);

  const response = await authenticatedFetch(
    `/api/reports/campaign/${encodeURIComponent(campaignName)}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch report: ${response.statusText}`);
  }

  const data: BackendReport = await response.json();
  console.log('📦 Report data:', data);

  // Transform backend issues to frontend format
  const transformedIssues: FrontendIssue[] = data.issues
    .filter((issue) => !issue.passed && issue.status !== 'dismissed')
    .map((issue, index) => {
      // Transform screenshots
      const screenshots = issue.relevant_screenshots?.map((screenshot, screenshotIndex) => {
        if (typeof screenshot === 'object' && 'image_data' in screenshot) {
          return {
            id: `screenshot-${data.id}-${index}-${screenshotIndex}`,
            url: screenshot.image_data,
            timestamp: screenshot.timecode,
            timestampSeconds: screenshot.timestamp_seconds,
            frameNumber: screenshot.frame_number,
            videoFile: screenshot.video_file,
          };
        }
        return {
          id: `screenshot-${data.id}-${index}-${screenshotIndex}`,
          url: screenshot,
        };
      });

      return {
        id: `${data.id}-${index}`,
        fileNames: issue.relevant_files || [],
        category: 'Validation Issue',
        flag: issue.severity,
        timestamps: [],
        description: issue.message,
        details: issue.details,
        status: issue.status === 'accepted' ? 'approved' : issue.status,
        screenshots,
      };
    });

  console.log('✅ Transformed issues:', transformedIssues);

  // Construct GCS path
  const bucketName = 'bkt-vmlmap-agentic-dev-content-agents';
  const gcsPath = `${bucketName}/campaign_packages/${data.campaign_package_name}`;

  return {
    reportId: data.id,
    packageName: data.campaign_package_name,
    gcsPath,
    issues: transformedIssues,
    notes: data.notes ?? null,
  };
};

/**
 * Fetch all reports (for reports list view)
 */
export const fetchReports = async (limit = 100): Promise<BackendReport[]> => {
  const response = await authenticatedFetch(`/api/reports/?limit=${limit}`);

  if (!response.ok) {
    throw new Error('Failed to fetch reports');
  }

  return response.json();
};

/**
 * Update a report with new issue states and/or notes
 */
export const updateReport = async (
  reportId: string,
  packageName: string,
  issues: FrontendIssue[],
  notes?: string | null
): Promise<{ id: string }> => {
  console.log('💾 Saving report update to backend...');

  // Transform frontend issues back to backend format
  const backendIssues: IssueUpdate[] = issues.map((issue) => ({
    passed: false,
    message: issue.description,
    details: issue.details || '',
    severity: issue.flag,
    relevant_files: issue.fileNames.length > 0 ? issue.fileNames : null,
    relevant_screenshots:
      issue.screenshots && issue.screenshots.length > 0
        ? issue.screenshots.map((s) => {
            if (s.timestampSeconds !== undefined && s.frameNumber !== undefined && s.timestamp) {
              return {
                image_data: s.url,
                timestamp_seconds: s.timestampSeconds,
                frame_number: s.frameNumber,
                timecode: s.timestamp,
                video_file: s.videoFile,
              };
            }
            return s.url;
          })
        : null,
    status: issue.status,
  }));

  const response = await authenticatedFetch(`/api/reports/${reportId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      campaign_package_name: packageName,
      issues: backendIssues,
      updated_by: 'user',
      ...(notes !== undefined && { notes }),
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save report: ${response.statusText}`);
  }

  const savedReport = await response.json();
  console.log('✅ Report saved (in-place update):', savedReport.id);

  return { id: savedReport.id };
};

/**
 * Fetch package summary by report ID
 */
export const fetchPackageSummaryById = async (
  reportId: string
): Promise<PackageSummaryResponse> => {
  console.log('📊 Fetching package summary for report:', reportId);

  const response = await authenticatedFetch(
    `/api/reports/${encodeURIComponent(reportId)}/summary`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch package summary: ${response.statusText}`);
  }

  const data: PackageSummaryResponse = await response.json();
  console.log('📦 Package summary data:', data);

  return data;
};

/**
 * Fetch package summary for a campaign (legacy - fetches summary for latest report)
 */
export const fetchPackageSummary = async (
  campaignName: string
): Promise<PackageSummaryResponse> => {
  console.log('📊 Fetching package summary for campaign:', campaignName);

  const response = await authenticatedFetch(
    `/api/reports/campaign/${encodeURIComponent(campaignName)}/summary`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch package summary: ${response.statusText}`);
  }

  const data: PackageSummaryResponse = await response.json();
  console.log('📦 Package summary data:', data);

  return data;
};
